/**
 * The birthday ask (design 2026-10-26, Claude outputs/birthday-ask-2026-10-26):
 * one plain question (the date), time and place folded behind one row, a
 * "What's this?" explainer, and a payoff read from the signs the server
 * returns on save (POST /birth/moment/ -> {moment, signs}). Pure: the
 * components (BirthMoment, BirthPayoff, BirthAsk) and their specs lean on it.
 *
 * No money anywhere here (the newcomer rule), and no pronouns: names or "you".
 */
import { WINDOWS } from '@/utils/birth-moment'

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December']

export const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius',
  'Capricorn', 'Aquarius', 'Pisces']
// Text-style glyphs (VS15), never the colour emoji.
export const GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((g) => g + '︎')

// Every string the ask, the closer look and the payoff say (DESIGN.md, copy table).
export const COPY = {
  sheetTitle: 'Find your star sign',
  whyAltar: 'Your birthday is all it takes, and it opens your personalized horoscope too.',
  whyGift: (name) => `It shows me your star sign, and how you and ${name} fit together.`,
  q: "What's this?",
  dateLabel: 'Your birthday',
  more: 'I know my birth time too',
  moreSub: 'Optional. It shows your Moon and Rising.',
  saveDate: 'Show my star sign',
  saving: 'Reading the sky…',
  fine: 'Only the date is needed. You can add more any time.',
  // 2026-10-26: no public surface shows the date or place (reader_detail
  // returns only the Mystic's own birth date), so the ask may promise it.
  privacy: 'Your birth date and place stay private. Only your signs show on your Mirror.',
  closerTitle: 'A closer look',
  closerWhy: 'Your birth time and place show your Rising sign and settle your Moon. Add what you know. Each one saves on its own.',
  timeLabel: 'About what time were you born?',
  placeLabel: 'Where were you born?',
  optional: '· optional',
  arcIdle: 'Tap the part of the day',
  exactBtn: 'I know the exact time',
  unknownBtn: "I don't know",
  unknownRead: "That's fine. Your Sun sign doesn't need it.",
  placePh: 'Town or city',
  mapBtn: 'Pick it on a map',
  mapRow: 'Not listed? Pick it on a map',
  noMatch: 'No match yet. Pick it on a map',
  mapHelp: 'Tap where you were born',
  pinned: 'Pinned on the map',
  notNow: 'Not now',
  saveBoth: 'Show my Moon and Rising',
  saveTime: 'Save my time',
  savePlace: 'Save my birthplace',
  saveNone: 'Add a time or a place',
  needPlace: 'Add where you were born to see your Rising.',
  needTime: 'Add a time to see your Rising.',
  addMore: '✦ Add your time to see your Moon and Rising',
  settleRising: 'Know the exact minute? Add it to settle your Rising.',
  addItNow: 'Add it now',
  bigThreeEyebrow: 'Your big three',
  bigThree: 'Sun, Moon and Rising',
  bigThreeGloss: 'Saved to your Mirror. Your personalized horoscope now reads from all three.',
  sunEyebrow: 'Your Sun sign',
  openedEyebrow: 'Just opened for you',
}

export function daysInMonth (month, year) {
  const m = Number(month)
  if (!m) return 31
  const y = /^\d{4}$/.test(String(year || '')) ? Number(year) : 2000   // 2000: a leap year, so 29 Feb stays pickable
  return new Date(y, m, 0).getDate()
}

/** Month/Day/Year fields into the ISO date the form holds ('' until all three are there). */
export function isoFromParts ({ month, day, year } = {}) {
  if (!month || !day || !/^\d{4}$/.test(String(year || ''))) return ''
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function partsFromIso (iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!m) return { month: '', day: '', year: '' }
  return { month: String(Number(m[2])), day: String(Number(m[3])), year: m[1] }
}

export const WINDOW_SHORT = { early_morning: 'Early', morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', late_evening: 'Late' }

/** True when the form holds a usable time answer (a part of the day, a clock, or "I don't know"). */
export function timeAnswered (form) {
  if (form.timeKind === 'window') return WINDOWS.some((w) => w.key === form.windowKey)
  if (form.timeKind === 'exact') return /^\d{1,2}:\d{2}$/.test(form.exactTime || '')
  return form.timeKind === 'unknown'
}
const knownTime = (form) => timeAnswered(form) && form.timeKind !== 'unknown'
const placed = (form) => !!(form.place && form.place.latitude != null)

/** The closer look's button and hint, from what is filled in. */
export function closerAction (form) {
  const t = timeAnswered(form)
  const p = placed(form)
  if (knownTime(form) && p) return { label: COPY.saveBoth, hint: '', disabled: false }
  if (t && p) return { label: COPY.saveTime, hint: '', disabled: false }
  if (t) return { label: COPY.saveTime, hint: form.timeKind === 'unknown' ? '' : COPY.needPlace, disabled: false }
  if (p) return { label: COPY.savePlace, hint: COPY.needTime, disabled: false }
  return { label: COPY.saveNone, hint: '', disabled: true }
}

/** "6:15 am" from "06:15". */
export function fmt12 (hm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm || '')
  if (!m) return ''
  const h = Number(m[1])
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'am' : 'pm'}`
}

/** The window an exact clock time falls in. */
export function windowOfClock (hm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm || '')
  if (!m) return null
  const h = Number(m[1]) + Number(m[2]) / 60
  const ranges = { early_morning: [0, 7], morning: [7, 12], afternoon: [12, 17], evening: [17, 21], late_evening: [21, 24] }
  const key = Object.keys(ranges).find((k) => h >= ranges[k][0] && h < ranges[k][1])
  return WINDOWS.find((w) => w.key === key) || null
}

/** The arc's readout under it. */
export function arcReadout (form) {
  if (form.timeKind === 'unknown') return { idle: true, main: COPY.unknownRead, sub: '' }
  if (form.timeKind === 'window') {
    const w = WINDOWS.find((x) => x.key === form.windowKey)
    if (w) return { idle: false, main: w.label, sub: w.hint }
  }
  if (form.timeKind === 'exact') {
    const w = windowOfClock(form.exactTime)
    if (w) return { idle: false, main: fmt12(form.exactTime), sub: w.label.toLowerCase() }
  }
  return { idle: true, main: COPY.arcIdle, sub: '' }
}

// ── The payoff ───────────────────────────────────────────────────────────────

const momentHasTime = (m) => !!(m && m.time && (m.time.kind === 'window' || m.time.kind === 'exact'))
const momentHasPlace = (m) => !!(m && m.place && m.place.latitude != null)

function missing (hasT, hasP) {
  if (!hasT && !hasP) return 'needs your time and place'
  if (hasT) return 'needs your birthplace'
  return 'needs your birth time'
}

/**
 * Sun, Moon and Rising as the payoff shows them, from the save response.
 * A sign the server claims is lit; "Virgo or Libra" is half (the time
 * settles it); nothing is locked, with what it needs.
 */
export function trio (signs, moment) {
  const s = signs || {}
  const hasT = momentHasTime(moment)
  const hasP = momentHasPlace(moment)
  const sun = s.sun || s.sun_text || ''
  const out = [{ key: 'sun', label: 'Sun', glyph: '☉︎', value: sun, sub: 'who you are at heart', state: sun ? 'lit' : 'locked' }]
  if (s.moon) out.push({ key: 'moon', label: 'Moon', glyph: '☽︎', value: s.moon, sub: 'your feelings', state: 'lit' })
  else if (s.moon_text) out.push({ key: 'moon', label: 'Moon', glyph: '☽︎', value: s.moon_text, sub: 'your time settles it', state: 'half' })
  else out.push({ key: 'moon', label: 'Moon', glyph: '☽︎', value: '?', sub: missing(hasT, hasP), state: 'locked' })
  if (s.rising) out.push({ key: 'rising', label: 'Rising', glyph: '↑︎', value: s.rising, sub: 'how you come across', state: 'lit' })
  else if (s.rising_text) out.push({ key: 'rising', label: 'Rising', glyph: '↑︎', value: s.rising_text, sub: 'the exact time settles it', state: 'half' })
  else out.push({ key: 'rising', label: 'Rising', glyph: '↑︎', value: '?', sub: missing(hasT, hasP), state: 'locked' })
  return out
}

/** Both the time and the place are in: the gold "add more" button goes. */
export function momentComplete (moment) { return momentHasTime(moment) && momentHasPlace(moment) }

/** Payoff 2's nudge and its action, or null when nothing is left to add. */
export function payoffNudge (signs, moment) {
  const s = signs || {}
  if (s.rising) return null
  if (s.rising_text) return { text: COPY.settleRising, action: COPY.exactBtn }
  return { text: momentHasTime(moment) ? COPY.needPlace : COPY.needTime, action: COPY.addItNow }
}

export function signIndex (sign) { return SIGNS.indexOf(sign) }

/** "Born 3 March: the Sun was in Pisces." */
export function bornGloss (iso, sun) {
  const p = partsFromIso(iso)
  if (!p.month || !sun) return ''
  return `Born ${Number(p.day)} ${MONTHS[Number(p.month) - 1]}: the Sun was in ${sun}.`
}

/**
 * "Just opened for you": only what this birthday really opened. What is open
 * is the server's: the Altar's tabs read again once the birthday is in
 * (`altarTabsAfter`). Not known, nothing is promised (e2e round 2, S22: the
 * Mirror wants the birth time and a name at Stage 1, Home the Moon).
 *   Your Mirror       its tab is there now and was not before (nav §13).
 *   Your personalized horoscope   a first birthday, with an Altar Home to read it on.
 *   You & <name>      the gift room only (Our Stars needs a second person).
 */
export function openedRows ({ context = 'altar', withName = '', firstBirthday = false, altarTabs = null, altarTabsAfter = null } = {}) {
  const rows = []
  const before = Array.isArray(altarTabs) ? altarTabs : []
  const after = Array.isArray(altarTabsAfter) ? altarTabsAfter : null
  if (firstBirthday && after && after.includes('mirror') && !before.includes('mirror')) {
    rows.push({ key: 'mirror', glyph: '☉︎', title: 'Your Mirror', text: 'Your signs, kept together in one place' })
  }
  if (firstBirthday && after && after.includes('altar')) {
    rows.push({ key: 'horoscope', glyph: '✦︎', title: 'Your personalized horoscope', text: 'On your Altar Home, fresh each day' })
  }
  if (context === 'gift' && withName) {
    rows.push({ key: 'together', glyph: '♡︎', title: `You & ${withName}`, text: 'How your two skies fit together' })
  }
  return rows
}

/**
 * The Sun sign from a date alone, for a screen the server does not answer
 * with signs (the birthday-request link). On a cusp day (the Sun changes sign
 * that day or the next) it says nothing rather than guess.
 */
export function sunSignOfDate (iso) {
  const p = partsFromIso(iso)
  if (!p.month) return ''
  const m = Number(p.month)
  const d = Number(p.day)
  // The day each month's second sign starts (Jan: Aquarius from the 20th).
  const cut = [20, 19, 21, 20, 21, 21, 23, 23, 23, 23, 22, 22]
  const first = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8]   // the sign before the cut (Jan: Capricorn)
  if (d === cut[m - 1] || d === cut[m - 1] - 1) return ''
  return SIGNS[d >= cut[m - 1] ? (first[m - 1] + 1) % 12 : first[m - 1]]
}

export function article (sign) { return /^[AEIOU]/.test(sign || '') ? 'an' : 'a' }

/** The Altar's tabs gained the Mirror (a birthday at Stage 1): its tab gets a dot. */
export function mirrorJustArrived (before, now) {
  return Array.isArray(before) && before.length > 0 && !before.includes('mirror') && Array.isArray(now) && now.includes('mirror')
}
